import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface PropertyPlanningFormProps {
  formValues: Record<string, any>;
  onFieldChange: (fieldId: string, value: any) => void;
}

const timelineOptions = [
  { id: 'less_1', label: '< 1 Jahr', years: 1 },
  { id: 'less_5', label: '< 5 Jahre', years: 5 },
  { id: 'less_10', label: '< 10 Jahre', years: 10 },
  { id: 'less_15', label: '< 15 Jahre', years: 15 },
  { id: 'more_20', label: '> 20 Jahre', years: 20 },
];

export function PropertyPlanningForm({ formValues, onFieldChange }: PropertyPlanningFormProps) {
  // Values with defaults - percentages are now editable
  const propertyPrice = parseFloat(formValues.immobilien_kosten) || 0;
  const [additionalCostsPercent, setAdditionalCostsPercent] = useState(
    formValues.nebengebuehren_prozent ?? 10
  );
  const [equityRecommendedPercent, setEquityRecommendedPercent] = useState(
    formValues.eigenmittel_prozent ?? 20
  );
  const currentEquity = parseFloat(formValues.aktuelle_eigenmittel) || 0;
  const additionalEquity = parseFloat(formValues.zusaetzliche_eigenmittel) || 0;
  const selectedTimeline = formValues.realisierung_zeitraum || 'less_10';
  
  // Editable years (synced from timeline initially, but can be overridden)
  const selectedOption = timelineOptions.find(o => o.id === selectedTimeline);
  const [manualYears, setManualYears] = useState<number | null>(
    formValues.realisierung_jahre ?? null
  );
  
  // Calculate yearsToRealize - prefer manual value, fallback to timeline
  const yearsToRealize = manualYears ?? selectedOption?.years ?? 10;
  
  // Sync manual years when timeline changes (unless user has manually set it)
  useEffect(() => {
    if (formValues.realisierung_jahre === undefined && selectedOption) {
      setManualYears(selectedOption.years);
    }
  }, [selectedTimeline, selectedOption, formValues.realisierung_jahre]);

  // Persist percent changes
  const handleAdditionalCostsPercentChange = (value: number) => {
    setAdditionalCostsPercent(value);
    onFieldChange('nebengebuehren_prozent', value);
  };

  const handleEquityPercentChange = (value: number) => {
    setEquityRecommendedPercent(value);
    onFieldChange('eigenmittel_prozent', value);
  };

  const handleYearsChange = (years: number) => {
    setManualYears(years);
    onFieldChange('realisierung_jahre', years);
  };
  
  // Calculated values
  const additionalCosts = propertyPrice * (additionalCostsPercent / 100);
  const totalProjectCost = propertyPrice + additionalCosts;
  const recommendedEquity = totalProjectCost * (equityRecommendedPercent / 100);
  const financingNeed = totalProjectCost - recommendedEquity;
  const totalEquity = currentEquity + additionalEquity;
  const missingEquity = Math.max(0, recommendedEquity - totalEquity);
  
  const monthsToRealize = yearsToRealize * 12;
  const monthlySavingsRate = missingEquity > 0 ? missingEquity / monthsToRealize : 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Eigentum </span>
          <span className="text-primary">schaffen</span>
        </h2>
        <p className="text-lg italic text-muted-foreground mt-1 font-handwriting">
          Der planbare Weg zum Eigentum.
        </p>
      </div>

      {/* Projektplanung Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-primary text-primary-foreground px-4 py-2 flex justify-between items-center font-semibold">
          <span>Projektplanung</span>
          <span>€</span>
        </div>
        
        {/* Rows */}
        <div className="divide-y divide-border">
          <div className="flex items-center px-4 py-3 gap-4">
            <Label className="flex-1 text-sm">
              Wie hoch schätzen Sie die Kosten Ihrer Wunschimmobilie?
            </Label>
            <Input
              type="text"
              inputMode="numeric"
              value={formValues.immobilien_kosten || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                onFieldChange('immobilien_kosten', value);
              }}
              placeholder="300.000"
              className="w-32 text-right"
            />
          </div>
          
          <div className="flex items-center px-4 py-3 gap-4">
            <div className="flex-1 text-sm text-muted-foreground flex items-center gap-1">
              <span>+</span>
              <Input
                type="text"
                inputMode="numeric"
                value={additionalCostsPercent}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  handleAdditionalCostsPercentChange(val === '' ? 0 : Number(val));
                }}
                className="w-12 h-7 text-sm text-center"
              />
              <span>% Nebengebühren</span>
            </div>
            <span className="w-32 text-right text-sm">{formatCurrency(additionalCosts)}</span>
          </div>
          
          <div className="flex items-center px-4 py-3 gap-4">
            <div className="flex-1 text-sm">
              <span>- Empfohlene Eigenmittel (</span>
              <Input
                type="text"
                inputMode="numeric"
                value={equityRecommendedPercent}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  handleEquityPercentChange(val === '' ? 0 : Number(val));
                }}
                className="w-12 h-7 text-sm text-center inline-block align-middle mx-1"
              />
              <span>% der Projektkosten inkl. Nebengebühren)</span>
            </div>
            <span className="w-32 text-right text-sm">-{formatCurrency(recommendedEquity)}</span>
          </div>
          
          <div className="flex items-center px-4 py-3 gap-4 bg-muted/30">
            <span className="flex-1 font-semibold text-sm">Finanzierungsbedarf</span>
            <span className="w-32 text-right font-semibold text-sm">{formatCurrency(financingNeed)}</span>
          </div>
        </div>
      </div>

      {/* Eigenmittel Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-primary text-primary-foreground px-4 py-2 flex justify-between items-center font-semibold">
          <span>Eigenmittel</span>
          <span>€</span>
        </div>
        
        {/* Rows */}
        <div className="divide-y divide-border">
          <div className="flex items-center px-4 py-3 gap-4">
            <Label className="flex-1 text-sm">Aktuelle Eigenmittel</Label>
            <Input
              type="text"
              inputMode="numeric"
              value={formValues.aktuelle_eigenmittel || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                onFieldChange('aktuelle_eigenmittel', value);
              }}
              placeholder="20.000"
              className="w-32 text-right"
            />
          </div>
          
          <div className="flex items-center px-4 py-3 gap-4">
            <Label className="flex-1 text-sm">
              Wie viele zusätzlichen Eigenmittel aus Kautionen, Genossenschaftsanteilen, Schenkungen, etc. werden Ihnen zur Verfügung stehen?
            </Label>
            <Input
              type="text"
              inputMode="numeric"
              value={formValues.zusaetzliche_eigenmittel || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                onFieldChange('zusaetzliche_eigenmittel', value);
              }}
              placeholder="10.000"
              className="w-32 text-right"
            />
          </div>
          
          <div className="flex items-center px-4 py-3 gap-4 bg-muted/30">
            <span className="flex-1 text-sm">Summe Eigenmittel</span>
            <span className="w-32 text-right text-sm">{formatCurrency(totalEquity)}</span>
          </div>
          
          <div className="flex items-center px-4 py-3 gap-4 bg-muted/50">
            <span className="flex-1 font-semibold text-sm">Fehlende Eigenmittel</span>
            <span className="w-32 text-right font-semibold text-sm text-primary">
              {missingEquity > 0 ? formatCurrency(missingEquity) : '-'}
            </span>
          </div>
        </div>
      </div>

      {/* Timeline Slider */}
      <div className="space-y-4">
        <p className="font-semibold text-sm">Bis wann möchten Sie Ihre Immobilie realisieren?</p>
        
        <div className="relative pt-2 pb-8">
          {/* Timeline line */}
          <div className="absolute left-0 right-0 top-[calc(0.5rem+10px)] h-0.5 bg-muted-foreground/40" />
          
          {/* Timeline points */}
          <div className="relative flex justify-between items-start">
            {timelineOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onFieldChange('realisierung_zeitraum', option.id);
                  setManualYears(option.years);
                  onFieldChange('realisierung_jahre', option.years);
                }}
                className="flex flex-col items-center focus:outline-none group"
              >
                <div
                  className={cn(
                    "w-5 h-5 rounded-full border-2 transition-all bg-background z-10",
                    selectedTimeline === option.id
                      ? "border-primary bg-primary"
                      : "border-muted-foreground/60 hover:border-primary/50"
                  )}
                />
                <span
                  className={cn(
                    "text-xs whitespace-nowrap mt-3 transition-colors",
                    selectedTimeline === option.id
                      ? "text-foreground font-medium"
                      : "text-muted-foreground"
                  )}
                >
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Calculation Result */}
      {missingEquity > 0 && (
        <div className="space-y-2 pt-2">
          <p className="font-semibold text-sm">Wie erreichen Sie die benötigten Eigenmittel?</p>
          <p className="text-sm text-muted-foreground flex items-center gap-1 flex-wrap">
            <span>Fehlende Eigenmittel / Realisierung in</span>
            <Input
              type="text"
              inputMode="numeric"
              value={yearsToRealize}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                handleYearsChange(val === '' ? 1 : Math.max(1, Number(val)));
              }}
              className="w-12 h-7 text-sm text-center font-medium text-foreground"
            />
            <span>Jahren / 12 Monate</span>
          </p>
          <p className="text-sm">
            Benötigte monatliche Sparrate = <span className="font-semibold text-primary text-lg">{formatCurrency(monthlySavingsRate)} €</span>
          </p>
        </div>
      )}
    </div>
  );
}
